import React, { useState, useEffect } from 'react';
import createAxiosInstance from '../../utils/axiosConfig';
import { FrameworkCategory } from '../../utils/interfaces';
import { create_domain } from '../../utils/endpoints';
import Alert from './alerts/alert';

interface AddDomainSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  frameworkData: FrameworkCategory | null;
}

interface DomainFormData {
  category_id: number;
  title: string;
  description: string;
  is_enabled: boolean;
  is_active: boolean;
}

const AddDomainSidebar: React.FC<AddDomainSidebarProps> = ({
  isOpen,
  onClose,
  onSuccess,
  frameworkData
}) => {
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState<{ message: string; type: 'success' | 'danger' | 'warning'; show: boolean } | null>(null);
  const [formData, setFormData] = useState<DomainFormData>({
    category_id: 0,
    title: '',
    description: '',
    is_enabled: true,
    is_active: true
  });
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  // Set category_id when frameworkData changes
  useEffect(() => {
    if (frameworkData) {
      setFormData(prev => ({ ...prev, category_id: frameworkData.id }));
    }
  }, [frameworkData]);

  // Form validation
  const validate = () => {
    const newErrors: { [key: string]: string } = {};
    if (!formData.category_id || formData.category_id === 0) {
      newErrors.category_id = 'Category ID is required';
    }
    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    }
    if (!formData.description.trim()) {
      newErrors.description = 'Shortcode is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form changes
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  // Add domain
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    
    setLoading(true);
    try {
      const axiosInstance = createAxiosInstance();
      const response = await axiosInstance.post(create_domain, formData);
      
      if (response.status === 200 || response.status === 201) {
        setAlert({
          message: 'Domain added successfully!',
          type: 'success',
          show: true
        });
        
        // Reset form
        setFormData({
          category_id: frameworkData?.id || 0,
          title: '',
          description: '',
          is_enabled: true,
          is_active: true
        });
        
        onSuccess();
        
        // Close sidebar after a short delay
        setTimeout(() => {
          handleClose();
        }, 1500);
      }
    } catch (error) {
      console.error('Error adding domain:', error);
      setAlert({
        message: 'Error adding domain. Please try again.',
        type: 'danger',
        show: true
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      category_id: frameworkData?.id || 0,
      title: '',
      description: '',
      is_enabled: true,
      is_active: true
    });
    setErrors({});
    setLoading(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      <div
        className="offcanvas-backdrop fade show"
        onClick={handleClose}
      ></div>
      <div
        className="offcanvas offcanvas-end show"
        tabIndex={-1}
        style={{ 
          zIndex: 1050, 
          width: '40vw',
          maxWidth: '500px'
        }}
      >
        <div className="offcanvas-header" style={{backgroundColor: 'white', borderBottom: '1px solid #dee2e6'}}>
          <h5 className="offcanvas-title" style={{
            fontSize: '1.25rem',
            fontWeight: 'bold',
            color: '#183FCE'
          }}>
            Add New Domain
          </h5>
          <button
            type="button"
            className="btn-close"
            onClick={handleClose}
          ></button>
        </div>
        
        <div className="offcanvas-body">
          <form onSubmit={handleSubmit}>
            <div className="mb-3">
              <label className="form-label">Framework:</label>
              <div className={`form-control bg-light ${errors.category_id ? 'is-invalid' : ''}`} style={{ backgroundColor: '#f8f9fa' }}>
                {frameworkData?.title || `Loading framework... (ID: ${formData.category_id})`}
              </div>
              {errors.category_id && (
                <div className="invalid-feedback">{errors.category_id}</div>
              )}
              {frameworkData && (
                <small className="text-muted">Framework ID: {frameworkData.id}</small>
              )}
            </div>
            
            <div className="mb-3">
              <label htmlFor="domainTitle" className="form-label">Title:</label>
              <input
                type="text"
                className={`form-control bg-white ${errors.title ? 'is-invalid' : ''}`}
                id="domainTitle"
                name="title"
                value={formData.title}
                onChange={handleChange}
                disabled={loading}
              />
              {errors.title && (
                <div className="invalid-feedback">{errors.title}</div>
              )}
            </div>
            
            <div className="mb-3">
              <label htmlFor="domainDescription" className="form-label">Shortcode:</label>
              <input
                type="text"
                className={`form-control bg-white ${errors.description ? 'is-invalid' : ''}`}
                id="domainDescription"
                name="description"
                value={formData.description}
                onChange={handleChange}
                disabled={loading}
              />
              {errors.description && (
                <div className="invalid-feedback d-block text-danger" style={{fontSize: '1rem'}}>{errors.description}</div>
              )}
            </div>
            
            <div className="mb-3">
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="domainEnabled"
                  name="is_enabled"
                  checked={formData.is_enabled}
                  onChange={handleChange}
                  disabled={loading}
                />
                <label className="form-check-label" htmlFor="domainEnabled">
                  Enable Domain
                </label>
              </div>
            </div>
            
            <div className="mb-3">
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="domainActive"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleChange}
                  disabled={loading}
                />
                <label className="form-check-label" htmlFor="domainActive">
                  Mark as Active
                </label>
              </div>
            </div>
            
            <div className="d-flex justify-content-end gap-2 mt-4">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleClose}
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-rounded"
                disabled={loading}
              >
                {loading ? (
                  <div className="d-flex align-items-center">
                    <div className="spinner-border spinner-border-sm me-2" role="status"></div>
                    Adding...
                  </div>
                ) : (
                  'Add Domain'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Alert */}
      {alert && (
        <Alert
          message={alert.message}
          type={alert.type}
          show={alert.show}
          onClose={() => setAlert(null)}
        />
      )}
    </>
  );
};

export default AddDomainSidebar; 